"use client";

import { useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { useDisconnect } from "wagmi";
import { Links } from "@/components/core";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui";
import { useAuth } from "@/context/auth";
import { usePathname, useRouter } from "@/i18n/navigation";
import { getProfileLinksData } from "./profileLinksData";

interface ProfileLinksProps {
  role?: "USER" | "ADMIN" | "GUEST";
}

export const ProfileLinks = ({ role }: ProfileLinksProps) => {
  const t = useTranslations("profile");
  const pathname = usePathname();
  const router = useRouter();
  const isProfileRoot = pathname === "/profile";
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const { logout } = useAuth();
  const { disconnect } = useDisconnect();
  const queryClient = useQueryClient();

  const handleLogoutClick = () => {
    setIsLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = async () => {
    await logout();
    disconnect();
    queryClient.clear();
    setIsLogoutDialogOpen(false);
    router.push("/");
  };

  const handleLogoutCancel = () => {
    setIsLogoutDialogOpen(false);
  };

  const links = getProfileLinksData(t, role, !isProfileRoot, handleLogoutClick);

  return (
    <>
      <Links links={links} />

      <Dialog open={isLogoutDialogOpen} onOpenChange={setIsLogoutDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center text-sm">
              {t("links.logoutDialogTitle")}
            </DialogTitle>
            <DialogDescription className="text-center text-text-secondary">
              {t("links.logoutDialogDescription")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <div className="flex gap-2 sm:gap-2">
              <Button
                variant="bordered"
                onClick={handleLogoutCancel}
                className="flex-1"
              >
                {t("links.logoutDialogCancel")}
              </Button>
              <Button
                variant="solid"
                onClick={handleLogoutConfirm}
                colors={"primary"}
                className="flex-1"
              >
                {t("links.logoutDialogConfirm")}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
